/**
 * `/admin/*` — the two-pane administration frame (M8-P2 §6.1).
 *
 * The tree is monday's, minus what a self-hosted Eli Z Monday does not have (billing, AI
 * governance, marketplace apps, connections, cross-account copier, tidy-up). Everything an
 * admin may not do is **absent**, not disabled (F8 §8), and a non-admin never reaches this
 * component at all: the route renders F4's 403 screen instead.
 */
import { useMemo } from 'react';
import type { AdminSource } from './types';
import { ConnectionsScreen } from './connections/ConnectionsScreen';
import type { ConnectionsSource } from './connections/types';
import {
  AuditScreen,
  GeneralScreen,
  InvitationsScreen,
  PermissionsScreen,
  SecurityScreen,
  TeamsScreen,
  TokensScreen,
  UsageScreen,
  UsersScreen,
} from './screens';
import './admin.css';

export const ADMIN_SECTIONS = [
  { id: 'general', group: 'General', label: 'Account · Customization' },
  { id: 'users', group: 'Directory', label: 'Users' },
  { id: 'invitations', group: 'Directory', label: 'Invitations' },
  { id: 'teams', group: 'Directory', label: 'Teams' },
  { id: 'permissions', group: null, label: 'Permissions' },
  /** M6-A4. monday's rail reads **Connections ▸ Automation connections**; with one page
   *  behind it the group heading would be a heading over a single row, so it is flat. */
  { id: 'connections', group: null, label: 'Automation connections' },
  { id: 'security', group: null, label: 'Security' },
  { id: 'tokens', group: null, label: 'API tokens' },
  { id: 'audit', group: null, label: 'Audit log' },
  { id: 'usage', group: null, label: 'Usage stats' },
] as const;

export type AdminSectionId = (typeof ADMIN_SECTIONS)[number]['id'];

export function AdminShell(props: {
  source: AdminSource;
  /** M6-A4: the Connections page has its own narrow port (`connections/types.ts`). */
  connections: ConnectionsSource;
  section: AdminSectionId;
  onSection: (id: AdminSectionId) => void;
  onInvite?: () => void;
}): React.ReactElement {
  const grouped = useMemo(() => {
    const out: Array<{ group: string | null; items: Array<(typeof ADMIN_SECTIONS)[number]> }> = [];
    for (const section of ADMIN_SECTIONS) {
      const last = out[out.length - 1];
      if (last && last.group === section.group) last.items.push(section);
      else out.push({ group: section.group, items: [section] });
    }
    return out;
  }, []);

  return (
    <div className="ez-admin" data-testid="admin-shell">
      <nav className="ez-admin__rail" aria-label="Administration">
        <h2 className="ez-admin__rail-title">Administration</h2>
        {grouped.map((block, index) => (
          <div key={block.group ?? `block-${index}`}>
            {block.group ? <div className="ez-admin-filter__title">{block.group}</div> : null}
            {block.items.map((section) => (
              <button
                key={section.id}
                type="button"
                className="ez-admin__rail-item"
                aria-current={props.section === section.id ? 'page' : undefined}
                data-testid={`admin-rail-${section.id}`}
                onClick={() => props.onSection(section.id)}
              >
                {section.group === 'Directory' || section.group === null
                  ? section.label
                  : section.label}
              </button>
            ))}
          </div>
        ))}
      </nav>

      <main className="ez-admin__pane">
        <AdminSection
          source={props.source}
          connections={props.connections}
          section={props.section}
          onInvite={props.onInvite}
        />
      </main>
    </div>
  );
}

function AdminSection({
  source,
  connections,
  section,
  onInvite,
}: {
  source: AdminSource;
  connections: ConnectionsSource;
  section: AdminSectionId;
  onInvite?: (() => void) | undefined;
}): React.ReactElement {
  switch (section) {
    case 'general':
      return <GeneralScreen source={source} />;
    case 'invitations':
      return <InvitationsScreen source={source} {...(onInvite ? { onInvite } : {})} />;
    case 'teams':
      return <TeamsScreen source={source} />;
    case 'permissions':
      return <PermissionsScreen source={source} />;
    case 'connections':
      return <ConnectionsScreen source={connections} />;
    case 'security':
      return <SecurityScreen source={source} />;
    case 'tokens':
      return <TokensScreen source={source} />;
    case 'audit':
      return <AuditScreen source={source} />;
    case 'usage':
      return <UsageScreen source={source} />;
    case 'users':
    default:
      return <UsersScreen source={source} />;
  }
}
